import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { User, Car, Crown, LogOut, ChevronRight, Wallet, Wrench, FileText, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/expenses/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <PageHeader title="Driver Profile" subtitle="Your VecPadi account." icon={User} />

      <div className="rounded-3xl border border-zinc-200 bg-white p-6">
        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-emerald-500 text-white font-display text-2xl font-bold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold">{user?.name}</div>
            <div className="text-sm text-zinc-500">{user?.email}</div>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-bold ${user?.plan === "premium" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>
            {user?.plan === "premium" ? "PREMIUM" : "FREE"}
          </span>
        </div>
      </div>

      {user?.plan !== "premium" && (
        <button onClick={() => navigate("/app/premium")} data-testid="profile-upgrade"
          className="flex w-full items-center justify-between rounded-3xl bg-gradient-to-r from-emerald-500 to-emerald-600 p-5 text-white transition-transform hover:-translate-y-0.5">
          <div className="flex items-center gap-3"><Crown size={22} /><div className="text-left"><div className="font-display font-bold">Upgrade to Premium</div><div className="text-xs text-emerald-50">Unlock AI scan, unlimited assistant & more</div></div></div>
          <ChevronRight />
        </button>
      )}

      <div>
        <h3 className="mb-3 font-display font-semibold">My vehicles</h3>
        <div className="space-y-2">
          {vehicles.map((v) => (
            <div key={v.id} className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4" data-testid="profile-vehicle">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600"><Car size={20} /></div>
              <div className="flex-1"><div className="text-sm font-medium">{v.make} {v.model}</div><div className="text-xs text-zinc-400">{v.year} • {v.plate} • {v.mileage?.toLocaleString()} km</div></div>
              <span className="text-sm font-bold text-emerald-600">{v.health_score}</span>
            </div>
          ))}
          <Button variant="outline" onClick={() => navigate("/onboarding")} className="w-full rounded-full">+ Add vehicle</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          { icon: Wallet, label: "Expenses", to: "/app/expenses" },
          { icon: Wrench, label: "Maintenance", to: "/app/care" },
          { icon: FileText, label: "Documents", to: "/app/care" },
          { icon: ShieldCheck, label: "Insurance", to: "/app/insurance" },
        ].map((x) => (
          <button key={x.label} onClick={() => navigate(x.to)} className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4 text-sm font-medium hover:bg-zinc-50">
            <x.icon size={18} className="text-emerald-600" /> {x.label}
          </button>
        ))}
      </div>

      <Button data-testid="profile-logout" onClick={doLogout} variant="outline" className="w-full rounded-full text-red-600 border-red-200 hover:bg-red-50 gap-2">
        <LogOut size={16} /> Log out
      </Button>
    </div>
  );
}
